import React, { useState, useEffect } from 'react';
import { useAuth } from '../auth/AuthContext';
import { authAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Loader2, Save, User, CheckCircle2, Lock } from 'lucide-react';

const NAVY = '#1e3a5f';

export default function ProfilePage() {
  const { user, refreshUser } = useAuth();
  const [form, setForm] = useState({
    psm_full_name: '', psm_job_title: 'Partner Success Manager',
    psm_direct_email: '', psm_direct_phone: '',
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        psm_full_name:    user.psm_full_name    || '',
        psm_job_title:    user.psm_job_title    || 'Partner Success Manager',
        psm_direct_email: user.psm_direct_email || '',
        psm_direct_phone: user.psm_direct_phone || '',
      });
    }
  }, [user]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.psm_full_name.trim()) { toast.error('Full name is required'); return; }
    if (!form.psm_direct_email.trim()) { toast.error('Contact email is required'); return; }
    setSaving(true);
    try {
      await authAPI.updateProfile(form);
      await refreshUser();
      setSaved(true);
      toast.success('Profile saved');
      setTimeout(() => setSaved(false), 3000);
    } catch { toast.error('Failed to save profile'); }
    finally { setSaving(false); }
  };

  const isComplete = !!(user?.psm_full_name && user?.psm_direct_email);

  return (
    <div className="max-w-xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-display)', color: NAVY }}>My Profile</h1>
        <p className="text-sm mt-1" style={{ color: 'hsl(var(--muted-fg))' }}>
          Your contact details appear in quote PDFs, signing emails, and resource downloads.
        </p>
      </div>

      {isComplete && (
        <div className="rounded-xl p-4 flex items-center gap-3" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
          <CheckCircle2 size={18} style={{ color: '#16a34a' }} className="flex-shrink-0" />
          <p className="text-sm" style={{ color: '#14532d' }}>
            Profile complete. Quotes and PDFs will be personalised with your contact details.
          </p>
        </div>
      )}

      {!isComplete && (
        <div className="rounded-xl p-4" style={{ background: '#fef3c7', border: '1px solid #fde68a' }}>
          <p className="text-sm" style={{ color: '#92400e' }}>
            Complete your name and contact email to personalise quotes, PDFs, and signing emails.
          </p>
        </div>
      )}

      {/* Login account - read only */}
      <div className="rounded-2xl border p-5 space-y-2" style={{ background: 'hsl(var(--muted))', borderColor: 'hsl(var(--border))' }}>
        <div className="flex items-center gap-2 mb-1">
          <Lock size={13} style={{ color: 'hsl(var(--muted-fg))' }} />
          <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'hsl(var(--muted-fg))' }}>Login account</p>
        </div>
        <div className="space-y-0.5">
          <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>Okta / SSO email</p>
          <p className="text-sm font-medium" style={{ color: 'hsl(var(--foreground))' }} data-testid="login-email-display">
            {user?.email || '-'}
          </p>
        </div>
        <p className="text-xs mt-1" style={{ color: 'hsl(var(--muted-fg))' }}>
          This is your system login. It cannot be changed here - contact your IT administrator.
        </p>
      </div>

      {/* PSM contact details - editable */}
      <form onSubmit={handleSave} className="rounded-2xl border bg-white p-6 space-y-5">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: NAVY }}>
            <User size={16} color="white" />
          </div>
          <div>
            <p className="font-semibold text-sm" style={{ color: NAVY }}>Contact &amp; business details</p>
            <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>Used in quote PDFs, signing emails, and resource downloads</p>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>Full name *</Label>
          <Input value={form.psm_full_name}
            onChange={e => setForm(f => ({ ...f, psm_full_name: e.target.value }))}
            placeholder="e.g. Nick Jackson" required />
        </div>

        <div className="space-y-1.5">
          <Label>Job title</Label>
          <Input value={form.psm_job_title}
            onChange={e => setForm(f => ({ ...f, psm_job_title: e.target.value }))}
            placeholder="Partner Success Manager" />
          <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>Defaults to "Partner Success Manager" if left blank.</p>
        </div>

        <div className="space-y-1.5">
          <Label>Contact / business email *</Label>
          <Input type="email" value={form.psm_direct_email}
            onChange={e => setForm(f => ({ ...f, psm_direct_email: e.target.value }))}
            placeholder="nick.jackson@ascarii.com" required
            data-testid="contact-email-input" />
          <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>
            This is the email shown on quotes and PDFs, and where customers can reply to signing emails.
            It can be different from your login email above.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label>Direct phone <span className="text-xs font-normal" style={{ color: 'hsl(var(--muted-fg))' }}>- optional</span></Label>
          <Input value={form.psm_direct_phone}
            onChange={e => setForm(f => ({ ...f, psm_direct_phone: e.target.value }))}
            placeholder="+44 7700 000000" />
        </div>

        {/* PDF footer preview */}
        {(form.psm_full_name || form.psm_direct_email) && (
          <div className="rounded-lg border p-3 space-y-1" style={{ background: '#f8fafc' }}>
            <p className="text-xs font-bold uppercase tracking-wide" style={{ color: '#64748b' }}>PDF footer preview</p>
            <p className="text-xs font-bold uppercase tracking-wider" style={{ color: '#0891b2', letterSpacing: '0.08em' }}>Your Partner Success Manager</p>
            <p className="text-xs" style={{ color: '#1e3a5f' }}>
              <strong>{form.psm_full_name || '[Your name]'}</strong>
              {form.psm_job_title && ` · ${form.psm_job_title}`}
              {form.psm_direct_email && ` · ${form.psm_direct_email}`}
              {form.psm_direct_phone && ` · ${form.psm_direct_phone}`}
            </p>
          </div>
        )}

        <Button type="submit" disabled={saving} className="w-full gap-2"
          style={{ background: NAVY, color: 'white' }}>
          {saving ? <Loader2 size={14} className="animate-spin" /> : saved ? <CheckCircle2 size={14} /> : <Save size={14} />}
          {saved ? 'Saved' : 'Save profile'}
        </Button>
      </form>
    </div>
  );
}
